import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

export type AppliedLinkInBioPage = {
  html?: string;
  title: string;
  pageTitle?: string;
  type?: string;
};

interface TemplatesTabProps {
  pageId?: string;
  onTemplateApplied?: (page: AppliedLinkInBioPage) => void;
}

export function TemplatesTab({ pageId, onTemplateApplied }: TemplatesTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [instagramUsername, setInstagramUsername] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isDialogOpen) return;

    void (async () => {
      try {
        const response = await fetch("/api/users/onboarding");
        if (!response.ok) return;
        const data = (await response.json()) as {
          instagramUsername?: string | null;
        };
        if (data.instagramUsername) {
          setInstagramUsername((current) => current || data.instagramUsername || "");
        }
      } catch (fetchError) {
        console.error("Templates username prefill error:", fetchError);
      }
    })();
  }, [isDialogOpen]);

  const handleOpenChange = (open: boolean) => {
    if (isApplying) return;
    setIsDialogOpen(open);
    if (!open) {
      setError(null);
    }
  };

  const handleApply = async () => {
    if (!pageId || !instagramUsername.trim() || isApplying) return;

    setIsApplying(true);
    setError(null);

    try {
      const response = await fetch(`/api/pages/${pageId}/apply-link-in-bio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instagramUsername: instagramUsername.trim() }),
      });

      const data = (await response.json()) as {
        page?: AppliedLinkInBioPage;
        error?: string;
      };

      if (!response.ok || !data.page) {
        throw new Error(data.error || "Failed to apply template");
      }

      onTemplateApplied?.(data.page);
      setIsDialogOpen(false);
      setError(null);
    } catch (applyError) {
      console.error("Apply link in bio template error:", applyError);
      setError(
        applyError instanceof Error
          ? applyError.message
          : "Failed to apply template"
      );
    } finally {
      setIsApplying(false);
    }
  };

  if (!pageId) {
    return (
      <div className="flex-1 overflow-auto p-4 text-sm text-muted-foreground">
        Save the page first to apply a template.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(10rem,10rem))] gap-4">
        <button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          className="flex aspect-square w-40 flex-col items-center justify-center rounded-lg border bg-card p-4 text-center text-sm font-medium transition hover:border-blue-600 hover:text-blue-600"
        >
          Link in Bio
        </button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link in Bio</DialogTitle>
            <DialogDescription>
              Enter your Instagram username to recreate this page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="link-in-bio-username">Instagram username</Label>
            <Input
              id="link-in-bio-username"
              value={instagramUsername}
              onChange={(e) => setInstagramUsername(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && instagramUsername.trim() && !isApplying) {
                  void handleApply();
                }
              }}
              placeholder="yourname"
              disabled={isApplying}
              autoFocus
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isApplying}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleApply()}
              disabled={!instagramUsername.trim() || isApplying}
            >
              {isApplying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
